import { Menu, Stack, Typography } from '@mui/material';
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { theme } from "src/theme/mui-theme";
import { projectsUiSlice } from "src/api/projects-ui-slice";
import { useAppDispatch, useAppSelector } from "src/app/hooks";
import { ts } from 'src/utils/translate';
import { DEFAULT_SPACING } from 'src/theme/style-consts';

type Props = {
    anchorEl: HTMLElement | null,
    handleClose: () => void
};

export function DateRangePickerView({ anchorEl, handleClose }: Props) {
    const open = Boolean(anchorEl);

    const dispatch = useAppDispatch();
    const projectsUiState = useAppSelector((state) => state.projectsUi);

    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
    const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().add(1, "day"));

    const today = dayjs().endOf('day');

    const handleStartChange = (newValue: Dayjs | null) => {
        setStartDate(newValue);
        if (newValue && endDate && newValue.isAfter(endDate)) {
            setEndDate(newValue);
        }
    };

    const handleEndChange = (newValue: Dayjs | null) => {
        if (newValue && startDate && newValue.isBefore(startDate)) return;
        setEndDate(newValue);
    };

    const handleFilter = () => {
        dispatch(projectsUiSlice.actions.setStartDate(startDate?.toISOString() || null));
        dispatch(projectsUiSlice.actions.setEndDate(endDate?.toISOString() || null));
        handleClose();
    };

    const handleClear = () => {
        setStartDate(dayjs());
        setEndDate(dayjs().add(1, "day"));
        dispatch(projectsUiSlice.actions.setStartDate(null));
        dispatch(projectsUiSlice.actions.setEndDate(null));
        handleClose();
    };

    const hasActiveFilter = !!(projectsUiState.startDate && projectsUiState.endDate);

    return (
        <Menu
            id="date-range-picker"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "center",
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack
                    spacing={DEFAULT_SPACING}
                    sx={{ px: DEFAULT_SPACING, pt: '0.25rem', pb: '0.75rem' }}
                >
                    <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary }}>
                        {ts('date_filter_created_title', 'Filter by created time')}
                    </Typography>
                    <Stack direction="row" alignItems="stretch" spacing="0.5rem">
                        <DatePicker
                            label={ts('start_date', 'Start date')}
                            value={startDate}
                            onChange={handleStartChange}
                            maxDate={today}
                            slotProps={{
                                textField: {
                                    sx: {
                                        "& .Mui-focused": {
                                            color: theme.palette.primary.main,
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            "&.Mui-focused fieldset": {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    },
                                },
                                popper: {
                                    sx: {
                                        "& .MuiPickersDay-root.Mui-selected": {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                        "& .MuiPickersDay-root.Mui-selected:hover": {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                    },
                                },
                            }}
                        />
                        <span style={{ fontSize: "1.25rem", color: "#555", alignSelf: "center" }}>–</span>
                        <DatePicker
                            label={ts('end_date', 'End date')}
                            value={endDate}
                            onChange={handleEndChange}
                            minDate={startDate || undefined}
                            slotProps={{
                                textField: {
                                    sx: {
                                        "& .Mui-focused": {
                                            color: theme.palette.primary.main,
                                        },
                                        "& .MuiOutlinedInput-root": {
                                            "&.Mui-focused fieldset": {
                                                borderColor: theme.palette.primary.main,
                                            },
                                        },
                                    },
                                },
                                popper: {
                                    sx: {
                                        "& .MuiPickersDay-root.Mui-selected": {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                        "& .MuiPickersDay-root.Mui-selected:hover": {
                                            backgroundColor: theme.palette.primary.main,
                                        },
                                    },
                                },
                            }}
                        />

                        {hasActiveFilter && (
                            <button
                                style={{
                                    marginLeft: "20px",
                                    padding: "10px 10px",
                                    backgroundColor: "#fff",
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    border: "none",
                                    cursor: "pointer",
                                }}
                                onClick={handleClear}
                            >
                                <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
                                    {ts('plt_cancel', 'Cancel')}
                                </Typography>
                            </button>
                        )}

                        <button
                            style={{
                                marginLeft: "10px",
                                padding: "10px 20px",
                                backgroundColor: "#fff",
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                border: "none",
                                cursor: "pointer",
                            }}
                            onClick={handleFilter}
                        >
                            <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
                                {ts('p_wifi_ok', 'OK')}
                            </Typography>
                        </button>
                    </Stack>
                </Stack>
            </LocalizationProvider>
        </Menu>
    )
}